import React, { useState } from 'react';
import { Modal, Form, Select, Input, message } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Customer } from '../../types/customers';
import LanguageSelector from '../common/LanguageSelector';
import CurrencySelector from '../common/CurrencySelector';
import { useQuery } from '@tanstack/react-query';
import { getCustomers } from '../../api/customers';

const { Option } = Select;

interface WorkspaceModalProps {
  open: boolean;
  onSave: (workspace: any) => void;
  onCancel: () => void;
  workspace?: any;
}

const WorkspaceModal: React.FC<WorkspaceModalProps> = ({ open, onSave, onCancel, workspace }) => {
  const { t } = useTranslation(['workspaces', 'common']);
  const [form] = Form.useForm();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => getCustomers(),
    enabled: open,
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  const customers = customersData?.customers || [];

  React.useEffect(() => {
    if (open) {
      if (workspace) {
        // Find the customer from the customers list using customer_id
        const customer = customers.find(c => c.id === workspace.customer_id);
        setSelectedCustomer(customer || null);
        form.setFieldsValue({
          customer_id: workspace.customer_id,
          status: workspace.status,
          presidentName: workspace.presidentName,
          presidentEmail: workspace.presidentEmail,
          presidentPhone: workspace.presidentPhone,
          language: workspace.language || 'en',
          currency: workspace.currency || 'USD',
          name: workspace.name,
          slug: workspace.slug,
        });
        console.log('Setting form values for editing:', {
          customer_id: workspace.customer_id,
          language: workspace.language || 'en',
          currency: workspace.currency || 'USD',
        });
      } else {
        setSelectedCustomer(null);
        form.resetFields();
      }
    }
  }, [open, workspace, form, customers]);

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId) || null;
    setSelectedCustomer(customer);
    if (customer) {
      const workspaceName = `${customer.property_name} Workspace`;
      const workspaceSlug = workspaceName.toLowerCase().replace(/\s+/g, '-');
      form.setFieldsValue({
        presidentName: customer.primary_contact?.name || '',
        presidentEmail: customer.primary_contact?.email || '',
        presidentPhone: customer.primary_contact?.phone || '',
        name: workspaceName,
        slug: workspaceSlug,
        language: 'en',
        currency: 'USD',
      });
    }
  };

  const handleOk = async () => {
    if (saving) return; // Prevent duplicate submissions
    
    try {
      setSaving(true);
      const values = await form.validateFields();
      console.log('Form values:', values); // Debug log
      const customer = customers.find(c => c.id === values.customer_id);
      if (!customer) {
        throw new Error(t('workspaces:form.customerRequired'));
      }

      if (workspace) {
        // For editing, just pass the values to parent
        const updatedWorkspace = { ...workspace, ...values };
        console.log('Updated workspace data:', updatedWorkspace); // Debug log
        onSave(updatedWorkspace);
      } else {
        // For new workspace, just pass the values to parent
        const newWorkspace = { ...values, customer_id: customer.id };
        console.log('New workspace data:', newWorkspace); // Debug log
        onSave(newWorkspace);
      }
      form.resetFields();
    } catch (e) {
      message.error(t('workspaces:form.saveError', 'Failed to save workspace'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={workspace ? t('workspaces:edit') : t('workspaces:add')}
      open={open}
      onOk={handleOk}
      onCancel={() => { form.resetFields(); onCancel(); }}
      width={600}
      destroyOnClose
      confirmLoading={saving}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="customer_id"
          label={t('workspaces:form.customer')}
          rules={[{ required: true, message: t('workspaces:form.customerRequired') }]}
        >
          <Select onChange={handleCustomerChange} value={selectedCustomer?.id}>
            {customers.map(customer => (
              <Option key={customer.id} value={customer.id}>{customer.property_name}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="name"
          label={t('workspaces:form.name', 'Workspace Name')}
          rules={[{ required: true, message: t('workspaces:form.nameRequired', 'Workspace name is required') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="slug"
          label={t('workspaces:form.slug', 'Workspace Slug')}
          rules={[{ required: true, message: t('workspaces:form.slugRequired', 'Workspace slug is required') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="status"
          label={t('workspaces:form.status')}
          rules={[{ required: true, message: t('workspaces:form.statusRequired') }]}
        >
          <Select>
            <Option value="active">{t('workspaces:status.active')}</Option>
            <Option value="inactive">{t('workspaces:status.inactive')}</Option>
            <Option value="pending">{t('workspaces:status.pending')}</Option>
            <Option value="suspended">{t('workspaces:status.suspended')}</Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="presidentName"
          label={t('workspaces:form.president')}
          rules={[{ required: true, message: t('workspaces:form.presidentRequired') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="presidentEmail"
          label={t('workspaces:form.presidentEmail')}
          rules={[
            { required: true, message: t('workspaces:form.presidentEmailRequired') },
            { type: 'email', message: t('workspaces:form.invalidEmail') }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="presidentPhone"
          label={t('workspaces:form.presidentPhone')}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="language"
          label={t('workspaces:settings.language')}
          rules={[{ required: true, message: t('workspaces:form.languageRequired') }]}
        >
          <LanguageSelector />
        </Form.Item>
        <Form.Item
          name="currency"
          label={t('workspaces:settings.currency')}
          rules={[{ required: true, message: t('workspaces:form.currencyRequired') }]}
        >
          <CurrencySelector />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default WorkspaceModal; 